import { useEffect, useRef, useState } from "react";
import type {
  CanonicalPractice,
  FeedTab,
  PracticeKey,
  SignalTypeFilter,
  SortValue,
  TriageCounts,
} from "../types";
import { useExportFeedCsv, useTriageCounts } from "../hooks";

/**
 * Redesign · header band (sealed mock). Deep-to-medium teal gradient carrying
 * the title + subtitle, the band actions (Export CSV + practice selector), and
 * six glass stat tiles. The "Last update" stamp moved to the nav logo block per
 * the mock, so the band no longer carries it.
 */

const TILES: {
  key: keyof Omit<TriageCounts, "updatedAt" | "latestSweepFinishedAt">;
  label: string;
  valueClass?: string;
}[] = [
  { key: "total", label: "TOTAL SIGNALS" },
  { key: "unclaimedTier1", label: "UNCLAIMED T1", valueClass: "sx-tile-val--light" },
  { key: "followUpsDue", label: "FOLLOW-UPS", valueClass: "sx-tile-val--gold" },
  { key: "claimedByMe", label: "CLAIMED BY ME" },
  { key: "saved", label: "SAVED" },
  { key: "archived", label: "ARCHIVED" },
];

export function SignalsHeader({
  viewerId,
  isAdmin,
  tab,
  signalType,
  sort,
  onSortChange,
  practices,
  practicesStatus,
  selectedPractice,
  onPracticeChange,
  onRetryPractices,
}: {
  viewerId: string;
  isAdmin: boolean;
  tab: FeedTab;
  signalType: SignalTypeFilter;
  sort: SortValue;
  onSortChange: (sort: SortValue) => void;
  /** Active canonical peer practices, in canonical display order (2W). */
  practices: CanonicalPractice[];
  practicesStatus: "pending" | "error" | "success";
  selectedPractice: PracticeKey | null;
  onPracticeChange: (practiceKey: PracticeKey | null) => void;
  onRetryPractices: () => void;
}): JSX.Element {
  const { data } = useTriageCounts(viewerId);
  const exportCsv = useExportFeedCsv();

  const exportLabel = exportCsv.isPending
    ? "Exporting…"
    : exportCsv.isSuccess
      ? "Exported"
      : "Export CSV";

  return (
    <header className="sx-band">
      <div className="sx-band-top">
        <div>
          <h1>Signals</h1>
          <p className="sx-band-sub">
            BUSINESS DEVELOPMENT SIGNALS ACROSS THE FIRM
          </p>
        </div>
        <div className="sx-band-actions">
          {isAdmin ? (
            <button
              type="button"
              className="sx-btn-onband"
              disabled={exportCsv.isPending}
              onClick={() =>
                exportCsv.mutate({
                  tab,
                  signalType,
                  viewerId,
                  practiceKey: selectedPractice,
                })
              }
            >
              {exportLabel}
            </button>
          ) : null}
          <SortControl sort={sort} onSortChange={onSortChange} />
          <PracticeSelector
            practices={practices}
            status={practicesStatus}
            selected={selectedPractice}
            onChange={onPracticeChange}
            onRetry={onRetryPractices}
          />
        </div>
      </div>

      <div className="sx-tiles">
        {TILES.map((t) => (
          <div key={t.key} className="sx-tile">
            <p className="sx-tile-lbl">{t.label}</p>
            <p className={`sx-tile-val${t.valueClass ? ` ${t.valueClass}` : ""}`}>
              {data ? data[t.key].toLocaleString() : "—"}
            </p>
          </div>
        ))}
      </div>
    </header>
  );
}

const SORT_OPTIONS: { value: SortValue; label: string }[] = [
  { value: "most_recent", label: "Most recent" },
  { value: "highest_score", label: "Highest score" },
];

/**
 * Sort control (Slice 2). The selected value is session-local page state (reset
 * to Most recent on a fresh visit) and enters ONLY the feed query. Custom
 * listbox mirroring the Practice selector's trigger + menu pattern; the value is
 * part of the feed query key, so switching restarts the cursor walk.
 */
function SortControl({
  sort,
  onSortChange,
}: {
  sort: SortValue;
  onSortChange: (sort: SortValue) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentLabel =
    SORT_OPTIONS.find((o) => o.value === sort)?.label ?? "Most recent";

  return (
    <div className="sx-sort" ref={ref}>
      <button
        type="button"
        className="sx-sort-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>Sort: {currentLabel}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <ul
        className="sx-sort-menu"
        role="listbox"
        aria-label="Sort signals"
        hidden={!open}
      >
        {SORT_OPTIONS.map((o) => (
          <li key={o.value} role="none">
            <button
              type="button"
              role="option"
              aria-selected={o.value === sort}
              className={`sx-sort-opt${o.value === sort ? " sx-sort-opt--on" : ""}`}
              onClick={() => {
                onSortChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Practice selector (2W). Mirrors the Sort control's accessible listbox
 * pattern exactly: button/listbox roles, aria-expanded, aria-selected,
 * outside-click + Escape to close, keyboard focus via native button tab
 * order. Every active internal user gets a working control — this panel is
 * not curator- or admin-gated (curator authority stays in the curator
 * console, not here).
 *
 * Three honest states beyond the normal open/closed trigger:
 *  - pending: the trigger is disabled ("Loading…") rather than showing a
 *    stale or empty option list.
 *  - error: the trigger becomes a retry affordance ("Unavailable — Retry");
 *    the firm-wide All feed underneath keeps working regardless.
 *  - success: the loaded registry drives the option list, in the order the
 *    server returned it — never hardcoded.
 */
function PracticeSelector({
  practices,
  status,
  selected,
  onChange,
  onRetry,
}: {
  practices: CanonicalPractice[];
  status: "pending" | "error" | "success";
  selected: PracticeKey | null;
  onChange: (practiceKey: PracticeKey | null) => void;
  onRetry: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (status === "error") {
    return (
      <div className="sx-practice">
        <button
          type="button"
          className="sx-practice-trigger sx-practice-trigger--error"
          onClick={onRetry}
          title="The practice list failed to load — click to retry"
        >
          <span>Practice: Unavailable — Retry</span>
        </button>
      </div>
    );
  }

  const loading = status === "pending";
  const currentLabel = selected
    ? practices.find((p) => p.practiceKey === selected)?.displayName ?? "All practices"
    : "All practices";

  return (
    <div className="sx-practice" ref={ref}>
      <button
        type="button"
        className="sx-practice-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={loading}
        disabled={loading}
        title={loading ? "Loading practices…" : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span>Practice: {loading ? "Loading…" : currentLabel}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <ul
        className="sx-practice-menu"
        role="listbox"
        aria-label="Filter by practice"
        hidden={!open}
      >
        <li role="none">
          <button
            type="button"
            role="option"
            aria-selected={selected === null}
            className="sx-pdrop"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            <span className="sx-tick" aria-hidden="true" />
            All practices
          </button>
        </li>
        {practices.map((p) => (
          <li key={p.practiceKey} role="none">
            <button
              type="button"
              role="option"
              aria-selected={p.practiceKey === selected}
              className="sx-pdrop"
              onClick={() => {
                onChange(p.practiceKey);
                setOpen(false);
              }}
            >
              <span className="sx-tick" aria-hidden="true" />
              {p.displayName}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
